import React from 'react';
import { FormattedMessage } from 'react-intl';

import { ProfileImage } from '@xingternal/profile-image';
import { ProfileInfo } from '@xingternal/profile-info';
import { BodyCopy, Headline, Meta } from '@xingternal/typography';
import { UserFlag } from '@xingternal/user-flag';

import { UserFlagsDisplayFlag } from '@/graphql/generated';
import { useBreakpoints } from '@/hooks';
import { ExtendedContactUser } from '@/types';

import * as S from './contact-component.styles';

type Props = {
  hint?: string | null;
  user?: ExtendedContactUser | null;
  connectionDegree: number;
  isCommonContact?: boolean;
};

const GENDER = {
  MALE: 'male-user',
  FEMALE: 'female-user',
  NONE: 'neutral-user',
  OTHER: 'neutral-user',
} as const;

const Contact: React.FC<Props> = ({ user, hint, connectionDegree, isCommonContact }) => {
  const { id, profileImage, displayName, occupations, slug, url = `/profile/${slug}`, gender, userFlags } = user ?? {};
  const username = displayName || `${user?.firstName} ${user?.lastName}`;
  const position = occupations?.[0]?.subline;
  const companyName = user?.occupations?.[0]?.headline;

  const testId = isCommonContact || connectionDegree === 1 ? 'first-degree-contact' : 'second-degree-contact';

  const { isMobile } = useBreakpoints();

  const visibleConnectionDegree = !isMobile ? connectionDegree : undefined;

  const onClickHandler = (): void => {
    window.location.assign(url);
  };
  return (
    <S.ContactWrapper data-testid={!isCommonContact ? testId : undefined} onClick={onClickHandler}>
      <ProfileInfo
        alignment="vertical"
        profileImage={
          isCommonContact ? (
            <a href={url} data-testid="first-degree-contact">
              <ProfileImage
                connectionDegree={visibleConnectionDegree}
                profileName={username}
                size="medium"
                src={profileImage?.[0]?.url}
                type={GENDER[gender ?? 'NONE']}
              />
            </a>
          ) : (
            <ProfileImage
              connectionDegree={visibleConnectionDegree}
              profileName={username}
              size="medium"
              src={profileImage?.[0]?.url}
              type={GENDER[gender ?? 'NONE']}
            />
          )
        }
      >
        <UserFlag
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore Executive flag not removed yet from X1
          displayFlag={userFlags?.displayFlag ?? UserFlagsDisplayFlag.Basic}
          size="medium"
          userId={id}
        />
        {isCommonContact ? (
          <S.CommonContactBodyCopy>
            <FormattedMessage
              id="JOBS_DETAIL_COMPANY_CONTACT_CONNECTION_HINT"
              values={{
                name: <Headline size="large">{username}</Headline>,
              }}
            />
          </S.CommonContactBodyCopy>
        ) : (
          <>
            <Headline size="large">{username}</Headline>
            {position ? <BodyCopy size="medium">{position}</BodyCopy> : null}
            {companyName ? <BodyCopy size="medium">{companyName}</BodyCopy> : null}
            {hint ? <Meta size="small">{hint}</Meta> : null}
          </>
        )}
      </ProfileInfo>
    </S.ContactWrapper>
  );
};

export default Contact;
